// @flow
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { identity, isEmpty } from "ramda";
import { useDebounce } from "react-use";
import { useDispatch, useSelector } from "react-redux";
import { IoIosCheckmark, IoIosClose } from "react-icons/io";
import Spinner from "react-spinkit";
import classNames from "classnames";

import { SelecaoCasasDecimais, SelecaoEscalaMoeda } from "../../configuracoes";
import BuscarAtivosSelecionaveis from "../../configuracoes/ativo_selecionavel/buscarAtivosSelecionaveis";
import { OkButton, IconButton } from "@quantum/ui";
import { Translate, useI18n } from "@quantum/i18n";

import { WithTooltip } from "../../../medidasSelecioandas/tooltip";

import {
  bindModalTrigger,
  useModal,
  SelectInput,
  Portal,
} from "../../../comum";
import ModalSintaxe from "./modal-sintaxe";
import { useValidacaoExpressaoSintaxe } from "../../../remote";
import {
  type ErrosSelecao,
  type MedidaSelecionada,
  MODO_APP_SERIES,
  ID_ESCALA_MOEDA_UNIDADES,
} from "../../../../model";
import {
  type State,
  adicionarMedidaParaPersonalizar,
  i18n,
  gerarAliasParaAtivo,
  getEdicaoMedida,
  getExpressao,
  getFormato,
  getFormatosMedidasCompostas,
  getIdMedidasParaPersonalizarDesabilitadasDeEdicao,
  getMedidaSendoEditada,
  getMedidasParaPersonalizar,
  getMedidasRankeaveis,
  getNomeMedidaComposta,
  removerMedidaParaPersonalizar,
  selecionaExpressao,
  selecionaFormatoMedidaComposta,
  selecionaNomeMedidaComposta,
  getModoApp,
} from "../../../../state";

// $FlowFixMe
import { ReactComponent as IconeAdicionar } from "../../../../assets/icone_adicionar.svg";
// $FlowFixMe
import { ReactComponent as IconeAdicionarCinza } from "../../../../assets/icone_adicionar_cinza.svg";
import IconeExcluirMedida from "../../../../assets/excluir_medida.gif";

import "./MedidaComposta.css";

const FORMATO_INTEIRO = 0;
const FORMATO_MOEDA = 2;

export function processarCriacaoMedida(
  state: State,
  medidasSelecionadas: Array<MedidaSelecionada>
): Array<MedidaSelecionada> {
  const edicaoMedida = getEdicaoMedida(state);

  return medidasSelecionadas.map(medida => {
    return {
      ...medida,
      descricaoSelecao: null,
      label: edicaoMedida.nome || medida.medida.nome,
      expressao: edicaoMedida.expressao,
      formato: edicaoMedida.formato,
      casasDecimais:
        edicaoMedida.formato !== FORMATO_INTEIRO
          ? edicaoMedida.casasDecimais
          : null,
      escala:
        edicaoMedida.formato === FORMATO_MOEDA ? edicaoMedida.escala : null,
    };
  });
}

export const criaFuncaoValidacaoMedidaComposta = (
  state: State
) => (): ?ErrosSelecao => {
  const erros: ErrosSelecao = {};
  const { translate } = i18n(state);

  if (isEmpty(getNomeMedidaComposta(state)))
    erros.medidaCompostaSemNome = translate(
      "configuracaoRelatorio.erroMedidaCompostaSemNome"
    );
  if (isEmpty(getFormato(state)))
    erros.medidaCompostaSemFormato = translate(
      "configuracaoRelatorio.erroMedidaCompostaSemFormato"
    );
  if (isEmpty(getExpressao(state))) {
    erros.medidaCompostaSemExpressao = translate(
      "configuracaoRelatorio.erroMedidaCompostaSemExpressao"
    );
  }

  return isEmpty(erros) ? null : erros;
};

const MedidaComposta = () => {
  const dispatch = useDispatch();
  const { translate } = useI18n();

  const [touched, setTouched] = useState({});
  const [ativoSelecionado, setAtivoSelecionado] = useState(null);

  const [aliasExibicao, setAliasExibicao] = useState(null);
  const [aliasMedidaSelecionada, setAliasMedidaSelecionada] = useState(null);

  const aliasAtivoDisponivel = useSelector(gerarAliasParaAtivo);
  const expressao = useSelector(getExpressao);
  const formato = useSelector(getFormato);
  const nome = useSelector(getNomeMedidaComposta);

  const formatos = useSelector(getFormatosMedidasCompostas);
  const medidasParaPersonalizar = useSelector(getMedidasParaPersonalizar);

  const medidasRankeaveis = useSelector(getMedidasRankeaveis);
  const medidas = medidasRankeaveis.map(medida => ({
    alias: medida.alias,
    label: medida.label || medida.descricaoSelecao,
  }));

  const idsMedidasParaPersonalizarDesabilitadas = useSelector(
    getIdMedidasParaPersonalizarDesabilitadasDeEdicao
  );

  const estaEditando = useSelector(getMedidaSendoEditada) != null;
  const modoApp = useSelector(getModoApp);
  const ref = useRef();

  const modalSintaxe = useModal("sintaxe");

  const expressaoParaValidar = useMemo(
    () => ({ expressao, medidasSelecionadas: medidasRankeaveis }),
    [expressao, medidasRankeaveis]
  );

  const adicionarMedidasDisabled = useMemo(
    () => medidas && medidas.length === 0,
    [medidas]
  );

  const resetarSelecaoMedida = () => {
    setAtivoSelecionado(null);
    setAliasMedidaSelecionada(null);
    setAliasExibicao(null);

    if (ref.current) {
      ref.current.resetBusca();
    }
  };

  const aoSelecionarMedida = aliasMedida => {
    setAliasMedidaSelecionada(aliasMedida);
    setAliasExibicao(ativoSelecionado ? aliasAtivoDisponivel : aliasMedida);
  };

  const aoAdicionarMedidaParaPersonalizar = useCallback(() => {
    if (!aliasMedidaSelecionada) {
      return;
    }

    const medida = medidas.find(
      ({ alias }) => aliasMedidaSelecionada === alias
    );

    const ativo = ativoSelecionado
      ? {
          alias: aliasAtivoDisponivel,
          tipoItemSelecionavel: ativoSelecionado.tipoItemSelecionavel,
          nome: ativoSelecionado.nome,
        }
      : null;

    const medidaParaPersonalizar = {
      id: `${medida.alias}++${ativo ? ativo.alias : "-"}`,
      ativo,
      medida,
    };

    dispatch(adicionarMedidaParaPersonalizar(medidaParaPersonalizar));

    resetarSelecaoMedida();
  }, [
    dispatch,
    aliasMedidaSelecionada,
    aliasAtivoDisponivel,
    ativoSelecionado,
    medidas,
  ]);

  const aoExcluirMedidaParaPersonalizar = useCallback(
    (event: SyntheticEvent<HTMLTextAreaElement>) => {
      event.stopPropagation();

      const idMedidaParaPersonalizar =
        event.currentTarget.dataset.idMedidaPersonalizar;

      dispatch(removerMedidaParaPersonalizar(idMedidaParaPersonalizar));
    },
    [dispatch]
  );

  const aoMudarFormula = useCallback(
    (event: SyntheticEvent<HTMLTextAreaElement>) => {
      const expressao = event.currentTarget.value;
      dispatch(selecionaExpressao(expressao));
    },
    [dispatch]
  );

  const aoMudarNome = useCallback(
    (event: SyntheticEvent<HTMLTextAreaElement>) => {
      const nome = event.currentTarget.value;
      dispatch(selecionaNomeMedidaComposta(nome));
      setTouched(touched => ({ ...touched, nome: true }));
    },
    [dispatch]
  );

  const aoMudarFormato = useCallback(
    formato => dispatch(selecionaFormatoMedidaComposta(formato)),
    [dispatch]
  );

  const {
    data: expressaoValidada,
    loading,
    loaded,
    failed,
    error,
    fetchData,
  } = useValidacaoExpressaoSintaxe({
    fetch: false,
    properties: expressaoParaValidar,
  });

  const onSelecionarAtivo = ativo => {
    const ativoSelecionado = ativo ? ativo : null;
    setAtivoSelecionado(ativoSelecionado);
    setAliasExibicao(aliasAtivoDisponivel);
  };

  useEffect(() => {
    if (!touched.nome && !estaEditando && loaded) {
      dispatch(selecionaNomeMedidaComposta(expressao));
    }
  }, [dispatch, touched.nome, loaded, expressao, estaEditando]);

  const [, cancelarDebounce] = useDebounce(
    () => expressao.trim() && fetchData(),
    3000,
    [expressao]
  );

  const aoTerminarDigitacaoFormula = useCallback(() => {
    cancelarDebounce();
    if (expressaoValidada !== expressao && expressao.trim()) fetchData();
  }, [fetchData, cancelarDebounce, expressaoValidada, expressao]);

  const aoClicarNoAlias = useCallback(
    event => {
      const alias = event.currentTarget.dataset.alias;
      const novaExpressao = expressao.endsWith(" ")
        ? expressao + alias
        : expressao + " " + alias;
      dispatch(selecionaExpressao(novaExpressao));
    },
    [dispatch, expressao]
  );

  return (
    <div className="MedidaComposta">
      <div className="MedidaComposta__medidas">
        <div className="MedidaComposta__form-medida-variavel">
          <div className="MedidaComposta__campo-variavel">
            <label className="MedidaComposta__label">
              <Translate>configuracaoRelatorio.variavel</Translate>
            </label>
            <label className="MedidaComposta__variavel">
              {aliasExibicao || "-"}
            </label>
          </div>

          {modoApp === MODO_APP_SERIES && (
            <div className="MedidaComposta__campo-ativos">
              <label className="MedidaComposta__label">
                <Translate>ItemQuantum.ATIVO.Plural</Translate>
              </label>
              <div className="MedidaComposta__busca-ativos">
                <BuscarAtivosSelecionaveis
                  ref={ref}
                  setarInputAoSelecionarAtivo={true}
                  placeholder={translate("Comum.Todos")}
                  selecionarAtivo={onSelecionarAtivo}
                />
              </div>
            </div>
          )}

          <div
            className={classNames(
              "MedidaComposta__campo-medidas",
              modoApp !== MODO_APP_SERIES
                ? "MedidaComposta__campo-medidas--expandido"
                : ""
            )}
          >
            <label className="MedidaComposta__label">
              <Translate>maiusculas.medidas</Translate>
            </label>
            <SelectInput
              className="MedidaComposta__combo-medidas"
              value={aliasMedidaSelecionada}
              onChange={aoSelecionarMedida}
              options={medidas}
              campoId="alias"
              campoDescricao="label"
              disabled={adicionarMedidasDisabled}
            />
          </div>

          <div className="MedidaComposta__campo-botao-adicionar">
            <IconButton
              icon={
                adicionarMedidasDisabled ? (
                  <IconeAdicionarCinza />
                ) : (
                  <IconeAdicionar />
                )
              }
              className="AdicionarMedida__botao"
              onClick={
                adicionarMedidasDisabled
                  ? identity
                  : aoAdicionarMedidaParaPersonalizar
              }
              disabled={adicionarMedidasDisabled}
            >
              <Translate>Comum.Adicionar</Translate>
            </IconButton>
          </div>
        </div>

        <table className="MedidaComposta__tabela">
          <thead>
            <tr>
              <td className="MedidaComposta__coluna-variavel">
                <Translate>configuracaoRelatorio.variavel</Translate>
              </td>
              {modoApp === MODO_APP_SERIES && (
                <td className="MedidaComposta__coluna-ativo">
                  <Translate>maiusculas.ativo</Translate>
                </td>
              )}
              <td className="MedidaComposta__coluna-medida">
                <Translate>maiusculas.medidas</Translate>
              </td>
              <td className="MedidaComposta__coluna-acao"></td>
            </tr>
          </thead>

          <tbody>
            {medidasParaPersonalizar.length === 0 && (
              <tr>
                <td colSpan={modoApp === MODO_APP_SERIES ? 4 : 3}>
                  <Translate>Comum.Msg.ConfigurePeloMenosUmaMedida</Translate>
                </td>
              </tr>
            )}

            {medidasParaPersonalizar.map(({ id, ativo, medida }) => {
              const alias = ativo ? ativo.alias : medida.alias;
              const edicaoMedidaDesabilitada = idsMedidasParaPersonalizarDesabilitadas.has(
                id
              );
              return (
                <tr key={id} onClick={aoClicarNoAlias} data-alias={alias}>
                  <td className="MedidaComposta__coluna-variavel">{alias}</td>
                  {modoApp === MODO_APP_SERIES && (
                    <td className="MedidaComposta__coluna-ativo">
                      {ativo ? ativo.nome : translate("Comum.Todos")}
                    </td>
                  )}
                  <td className="MedidaComposta__coluna-medida">
                    {medida.label}
                  </td>
                  <td
                    className="MedidaComposta__coluna-acao"
                    onClick={
                      edicaoMedidaDesabilitada
                        ? identity
                        : aoExcluirMedidaParaPersonalizar
                    }
                    data-id-medida-personalizar={id}
                  >
                    <WithTooltip
                      enabled={edicaoMedidaDesabilitada}
                      trigger="hover"
                      overlayClassName="MedidaComposta__tooltip-remover"
                      placement="topRight"
                      overlay={
                        <span>
                          <Translate>
                            configuracaoRelatorio.medidaNaoPodeSerExcluida
                          </Translate>
                        </span>
                      }
                    >
                      <div
                        className={classNames(
                          "MedidaComposta__acao-com-icone",
                          edicaoMedidaDesabilitada &&
                            "MedidaComposta__acao-com-icone--desabilitada"
                        )}
                      >
                        <img src={IconeExcluirMedida} alt="" />
                      </div>
                    </WithTooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="MedidaComposta__form-nova-medida">
        <Portal element={document.querySelector("#modalSintaxe")}>
          <ModalSintaxe />
        </Portal>
        <div className="MedidaComposta__campo-formula-container">
          <label className="MedidaComposta__label">
            <Translate>configuracaoRelatorio.funcao</Translate>
          </label>
          <textarea
            placeholder={translate(
              "configuracaoRelatorio.expressaoPlaceholder"
            )}
            className="MedidaComposta__campo-formula"
            onChange={aoMudarFormula}
            onBlur={aoTerminarDigitacaoFormula}
            value={expressao}
          />
        </div>
        <div className="MedidaComposta__formato-e-sintaxe">
          <div className="MedidaComposta__formatacao-container">
            <div className="MedidaComposta__campo-formato-container">
              <label className="MedidaComposta__label">
                <Translate>maiusculas.formato</Translate>
              </label>
              <SelectInput
                className="MedidaComposta__campo-formato"
                value={formato}
                onChange={aoMudarFormato}
                options={formatos}
                campoId="codigo"
                campoDescricao="traducao"
              />
            </div>
            {formato !== FORMATO_INTEIRO && (
              <SelecaoCasasDecimais id="casasDecimais" nomeMedida="Composta" />
            )}
            {modoApp === MODO_APP_SERIES && (
              <>
                {formato === FORMATO_MOEDA && (
                  <SelecaoEscalaMoeda
                    valorPadrao={ID_ESCALA_MOEDA_UNIDADES}
                    nomeMedida="Composta"
                  />
                )}
              </>
            )}
          </div>
          <OkButton
            className="MedidaComposta__botao-sintaxe"
            {...bindModalTrigger(modalSintaxe)}
          >
            <Translate>configuracaoRelatorio.sintaxe</Translate>
          </OkButton>
        </div>
        <div className="MedidaComposta__campo-nome-container">
          <label className="MedidaComposta__label">
            <Translate>maiusculas.nome</Translate>
          </label>
          <div className="MedidaComposta__campo-e-botao">
            <input
              className="MedidaComposta__campo-nome"
              onChange={aoMudarNome}
              placeholder={translate(
                "configuracaoRelatorio.nomeExibicaoFuncao"
              )}
              value={nome}
            />
          </div>
        </div>
        <div className="MedidaComposta__validacao-formato-container">
          {loading ? (
            <div className="MedidaComposta__validacao-texto">
              <Spinner
                name="ball-clip-rotate"
                className="MedidaComposta__validacao-spinner"
                fadeIn="none"
              />
              <div>
                <strong>
                  <Translate>configuracaoRelatorio.validando</Translate>.
                </strong>
                <Translate>compostas.validacao-carregando</Translate>
              </div>
            </div>
          ) : failed ? (
            <div className="MedidaComposta__validacao-texto MedidaComposta__validacao-texto--erro">
              <IconButton
                className="MedidaComposta__botao-tentar-validar-novamente"
                onClick={fetchData}
                icon={<IoIosClose />}
              >
                <strong>
                  <Translate>configuracaoRelatorio.funcaoInvalida</Translate>
                </strong>{" "}
                {error}{" "}
                <u>
                  <Translate>
                    configuracaoRelatorio.cliqueParaTentarNovamente
                  </Translate>
                  .
                </u>
              </IconButton>
            </div>
          ) : (
            loaded && (
              <div className="MedidaComposta__validacao-texto MedidaComposta__validacao-texto--sucesso">
                <IoIosCheckmark
                  className="MedidaComposta__validado-icone"
                  size="2rem"
                />
                <strong>
                  <Translate>configuracaoRelatorio.validado</Translate>
                </strong>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default MedidaComposta;
